import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import compareObjectKeys from '../../../functions/compareObjectKeys';
import './ArrangeableListInput.css';
import iconArrowUp from '../../../imgs/icon-arrow-up.svg';
import iconArrowDown from '../../../imgs/icon-arrow-down.svg';
import iconArrowUpMedGray from '../../../imgs/icon-arrow--up-med-gray.svg';
import iconArrowDownMedGray from '../../../imgs/icon-arrow--down-med-gray.svg';
import iconClose from '../../../imgs/icon-close--med-gray.svg';

export default class ArrangeableListInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      selectedItemId: null
    };
  }
  
  // Takes the options provided, translates into a standard object format, and sets as items state
  setItems = (newOptions) => {
    if (newOptions && newOptions.length) {
      let validItems = newOptions
        .filter((option) => (option && 
          typeof option === "object" && 
          option.id !== undefined && 
          option.text !== undefined
        ));

      if (validItems.length) {
        let numberedItems = validItems
          .filter((item) => (item.order && item.order > 0))
          .sort(compareObjectKeys("order"));

        let unnumberedItems = validItems
          .filter((item) => !(item.order && item.order > 0))
          .sort(compareObjectKeys("text", true));

        let items = [...unnumberedItems, ...numberedItems]
          .map((option) => { 
            return { id: option.id, internalLink: option.internalLink, text: option.text };
          });

        this.setState({ items });
      }   
    }
  }

  // Sets the id provided as the selectedId, or nullifies the selectedId if the one passed in matches
  selectItem = (id) => {
    let selectedItemId = (id !== this.state.selectedItemId) ? id : null;

    this.setState({ selectedItemId });
  }
  
  // Moves item in the direction provided to the function, updating the items array's order accordingly
  moveItem = (direction) => {
    let itemIndex = this.state.items.findIndex((item) => item.id === this.state.selectedItemId);
    
    let switchingItem = (direction === "up") ?
      itemIndex - 1 :
      itemIndex + 1;
    
    this.setState((prevState) => {
      let items = prevState.items;
      
      [items[itemIndex], items[switchingItem]] = [items[switchingItem], items[itemIndex]];
      
      return { items };
    });
  }
  
  addItem = (addedItem) => {
    let itemIsInList = this.state.items.find((item) => item.id === addedItem.id);
    
    if (!itemIsInList) {
      this.setState((prevState) => {
        let items = [...prevState.items, { id: addedItem.id, internalLink: addedItem.internalLink, text: addedItem.text }];

        return { items };
      });
    }
  }
  
  // Removes the selected item from the list
  removeItem = () => {
    let itemIndex = this.state.items.findIndex((item) => item.id === this.state.selectedItemId);
    
    let itemObject = this.state.items[itemIndex];
    
    if (itemIndex >= -1) {
      if (itemIndex + 1 !== this.state.items.length) {
        this.selectItem(this.state.items[itemIndex + 1].id);
      }
      else if (itemIndex !== 0) {
        this.selectItem(this.state.items[itemIndex - 1].id);
      }
      
      this.setState((prevState) => {
        if (prevState.items.length > 1) {
          prevState.items.splice(itemIndex, 1)
        }
        else {
          prevState.items = [];
        }

        return { items: prevState.items };
      }, () => {
        if (this.props.onRemove) {
          this.props.onRemove(itemObject);
        }
      });
    }
  }
  
  // Creates arrow based on the direction being provided and whether or not the selected item can be moved in that direction
  buildArrow = (direction) => {
    let indexToCheck = (direction === "up") ? 0 : this.state.items.length - 1;

     if (this.state.selectedItemId && this.state.items[indexToCheck].id !== this.state.selectedItemId) {
      let activeArrowSvg = (direction === "up") ? iconArrowUpMedGray : iconArrowDownMedGray;

      return (
        <div 
          className="arrangeable-list-input__arrow--enabled"
          onClick={() => this.moveItem(direction)}>
            <img src={activeArrowSvg} alt={direction} />
        </div>
      )
    }
    else {
      let arrowSvg = (direction === "up") ? iconArrowUp : iconArrowDown;

      return (
        <div className="arrangeable-list-input__arrow--disabled">
            <img src={arrowSvg} alt={direction} />
        </div>
      )
    }
  }
  
  // Creates remove button if hasRemoveButton is true
  buildRemoveButton = () => {
    let removeButton = (this.props.hasRemoveButton) ?
      <div 
        className="arrangeable-list-input__remove-button"
        onClick={() => this.removeItem()}
      >
        <img src={iconClose} alt="remove" />
      </div> :
      null;

    return removeButton;
  }
  
  // Gets the values of the arrangeable list input
  getValue = () => {
    let objectsWithOrder = 
      this.state.items
        .map((item, index) => { return { id: item.id, internalLink: item.internalLink, text: item.text, order: index + 1 } });

    return objectsWithOrder;
  }

  UNSAFE_componentWillMount() {
    this.setItems(this.props.options);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if ( 'options' in newProps && 
      newProps.options !== undefined && 
      newProps.options.length !== this.state.items.length
    ) {
      this.setItems(newProps.options);
    }
  }
  
  render() {
    if (!this.props.isEditable && this.state.selectedItemId) {
      this.selectItem(null);
    }
    
    let label = (this.props.label) ?
      <label htmlFor={this.props.id}>
        {this.props.label}
      </label> :
      null;
    
    let verticalSpacingClass = this.props.verticalSpacing === "compact" ? "field--compact" : "field";
    
    let arrows = (this.props.isEditable && this.state.items.length) ?
      <div className="arrangeable-list-input__arrows">
        {this.buildArrow("up")}
        {this.buildArrow("down")}
        {(this.state.selectedItemId) ? this.buildRemoveButton() : null}
      </div> : 
      null;
    
    let inputDisplay = (this.props.isEditable) ?
      <span>
        <div className={"arrangeable-list-input__box "}>
          <ul>
            {(this.state.items.length) ? this.state.items.map((item) => <li key={this.props.id + " " + item.id} 
              onClick={() => this.selectItem(item.id)} 
              className={(this.state.selectedItemId === item.id) ? "arrangeable-list-input__item--selected" : ""}>
                {item.text}
            </li>) : 
            <span className="arrangeable-list-input__no-content-message">There is no content to display.</span>}
          </ul>
        </div>
        {arrows}
      </span> :
      <span className="read-only">
        <ul>
          {this.state.items.map((item) => {
            let itemText = (item.internalLink) ? 
              <Link to={item.internalLink}>
                {item.text}
              </Link> : 
              item.text;
           
           return <li key={item.id}>{itemText}</li> 
          })}
        </ul>
      </span>;

    return (<div className={verticalSpacingClass + " arrangeable-list-input " + this.props.className} id={this.props.id} tabIndex="0" onBlur={() => { if (!(navigator.userAgent.indexOf("MSIE ") > -1 || navigator.userAgent.indexOf("Trident/") > -1)) { this.selectItem(null) }}}>
      {label}
      {inputDisplay}
    </div>);
  }
}

ArrangeableListInput.defaultProps = {
  hasRemoveButton: false,
  isEditable: true
};

ArrangeableListInput.propTypes = {
  className: PropTypes.string,
  hasRemoveButton: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  label: PropTypes.string,
  onRemove: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number]).isRequired,
    internalLink: PropTypes.string,
    order: PropTypes.number,
    text: PropTypes.string.isRequired
  })),
  verticalSpacing: PropTypes.string
}